import React, { useContext, useEffect, useState } from 'react';
import { Editor as SlateEditor, Element as SlateElement } from 'slate';
import { toggleBlock } from 'app/slate/functions';
import { useSlate } from 'slate-react';
import { i18n } from 'app/utils/i18n';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { toolbarCSSExtractor } from 'app/utils/css';
import { Dropdown } from 'app/components';
import { UserContext } from 'app/state/contexts/UserContext';

const STRUCTURAL_FORMAT_TO_TYPE = {
  li: 'list-item',
  ul: 'bulleted-list',
  ol: 'numbered-list',
};

export const getActiveBlockId = (node, formats, editor) => {
  const activeFormat = node?.formatting?.format;
  const activeClass = node?.formatting?.class;

  if ((activeFormat || activeClass) && Array.isArray(formats)) {
    // First try to match compound selectors (both format + class)
    const compoundMatch = formats.find((f) => f.value.format && f.value.class && f.value.format === activeFormat && f.value.class === activeClass);
    if (compoundMatch) return compoundMatch;

    // A class is more specific than a bare tag, so check class-only options
    // before format-only ones — otherwise a node like
    // { format: 'p', class: 'ingress' } matches the plain `p` option.
    const classMatch = activeClass && formats.find((f) => !f.value.format && f.value.class === activeClass);
    if (classMatch) {
      return classMatch;
    }

    const formatMatch = activeFormat && formats.find((f) => !f.value.class && f.value.format === activeFormat);
    if (formatMatch) {
      return formatMatch;
    }
  }

  // For structural list elements (li/ul/ol) the class lives on an ancestor of
  // the caret rather than the immediate parent that `node` represents. Walk
  // ancestors via the editor selection so the dropdown reflects classes set
  // on <li>, <ul>, or <ol> by `toggleBlock`.
  if (editor?.selection && Array.isArray(formats)) {
    for (const f of formats) {
      const targetType = STRUCTURAL_FORMAT_TO_TYPE[f.value?.format];
      if (!targetType) continue;
      const [ancestor] = SlateEditor.nodes(editor, {
        match: (n) => SlateElement.isElement(n) && n.type === targetType,
      });
      if (!ancestor) continue;
      const [ancestorNode] = ancestor;
      if (f.value.class) {
        if (ancestorNode.formatting?.class === f.value.class) return f;
      } else {
        // format-only structural option (e.g. plain `ul`/`ol`/`li` without a class)
        if (!ancestorNode.formatting?.class) return f;
      }
    }
  }

  return null;
};

/**
 * When the user clicks a new page that has a different template see if that template has new
 * typography options under the .toolbar class (this is done in toolbarCSSExtractor())
 *
 * @param {*} reduxActiveSection
 * @param {*} reduxDocumentCSS
 * @returns
 */
export const getOptionsForCurrentTemplate = (reduxActiveSection = {}, toolbarDefinition = []) => {
  if (!reduxActiveSection) {
    return [];
  }

  //Fetch global toolbar layout
  const active = toolbarDefinition.filter((toolbar) => toolbar.templateId === undefined || toolbar.templateId === reduxActiveSection.templateSuperId) ?? [];

  // Map toolbar definitions to dropdown options.
  const options = active.flatMap((toolbar, index) => {
    return toolbar.options.map((option) => {
      let entry = {
        id: index,
        value: {},
        title: option.title,
        requiredClasses: toolbar.wrapperClasses,
      };

      // Parse selector: "h5.ingress" → { format: 'h5', class: 'ingress' }
      // ".ingress" → { class: 'ingress' }
      // "h1" → { format: 'h1' }
      const dotIndex = option.selector.indexOf('.');
      if (dotIndex === 0) {
        // Starts with dot: class only
        entry.value['class'] = option.selector.substring(1);
      } else if (dotIndex > 0) {
        // Compound: tag.class
        entry.value['format'] = option.selector.substring(0, dotIndex);
        entry.value['class'] = option.selector.substring(dotIndex + 1);
      } else {
        // Plain HTML tag
        entry.value['format'] = option.selector;
      }
      return entry;
    });
  });

  return options;
};

const DropItemComponent = ({ item }) => {
  const cssClass = item.value?.class || '';
  let child = (
    <p className={`m-0 ${cssClass}`}>
      <span>{item.title}</span>
    </p>
  );

  switch (item.value?.format) {
    case 'h1':
      child = <h1 className={`fs-3 my-0 ${cssClass}`}>{item.title}</h1>;
      break;
    case 'h2':
      child = <h2 className={`fs-4 my-0 ${cssClass}`}>{item.title}</h2>;
      break;
    case 'h3':
      child = <h3 className={`fs-5 my-0 ${cssClass}`}>{item.title}</h3>;
      break;
    case 'h4':
      child = <h4 className={`fs-6 my-0 ${cssClass}`}>{item.title}</h4>;
      break;
    case 'h5':
      child = <h5 className={`fs-6 my-0 ${cssClass}`}>{item.title}</h5>;
      break;
    case 'h6':
      child = <h6 className={`fs-6 my-0 ${cssClass}`}>{item.title}</h6>;
      break;
    default:
      break;
  }

  let innerClass = !!item.requiredClasses ? item.requiredClasses[item.requiredClasses.length - 1] : '';
  return (
    <div className="page-wrapper force-text">
      <div className={innerClass}>{child}</div>
    </div>
  );
};

export const MarkHeadingDropdown = () => {
  const { user } = useContext(UserContext);
  const locale = user?.userProperties?.cultureId ?? 'sv';

  const defaultOptions = [
    {
      value: { format: 'paragraph' },
      title: i18n('dropdown.format.paragraph'),
    },
    {
      value: { format: 'h1' },
      title: i18n('dropdown.format.heading-1'),
    },
    {
      value: { format: 'h2' },
      title: i18n('dropdown.format.heading-2'),
    },
    {
      value: { format: 'h3' },
      title: i18n('dropdown.format.heading-3'),
    },
  ];
  const [value, setValue] = useState(defaultOptions[0].value);
  const [options, setOptions] = useState(defaultOptions);

  const placeholderValue = (opts) =>
    (opts.find((option) => !option.value.class && (option.value.format === 'p' || option.value.format === 'paragraph')) ?? opts[0])?.value;
  const editor = useSlate();

  const reduxActiveSection = useEditorSelector((editor) => editor.activeSection);
  const reduxActiveNode = useEditorSelector((editor) => editor.activeNode);
  const reduxDocumentCSS = useEditorSelector((editor) => editor.pDocument).css;

  useEffect(() => {
    const block = getActiveBlockId(reduxActiveNode, options, editor);
    setValue(block?.value || placeholderValue(options));
  }, [reduxActiveNode, options, editor]);

  useEffect(() => {
    if (reduxActiveSection?.type) {
      const templateOptions = getOptionsForCurrentTemplate(reduxActiveSection, toolbarCSSExtractor(reduxDocumentCSS, locale, 'toolbar'));
      const nextOptions = templateOptions.length > 0 ? templateOptions : defaultOptions;

      setOptions(nextOptions);
      setValue(placeholderValue(nextOptions));
    }
    // eslint-disable-next-line
  }, [reduxActiveSection, reduxDocumentCSS]);

  const handleChange = (newFormat) => {
    setValue(newFormat);
    toggleBlock(editor, 'custom-formatting', newFormat);
  };

  const handleSelect = (newFormat) => {
    if (STRUCTURAL_FORMAT_TO_TYPE[newFormat?.format] && JSON.stringify(newFormat) === JSON.stringify(value)) {
      toggleBlock(editor, 'custom-formatting', newFormat);
    }
  };

  return <Dropdown sm items={options} value={value} onChange={handleChange} onSelect={handleSelect} wrapper={DropItemComponent} filter="contains" />;
};
